// Mattress heights are stored in the DB as ranges ("10-15", "16-20", ... "41-50"), one Price row per
// range. Customers know their mattress is 13 cm, not which bucket that falls in, so the dropdown
// lists every centimetre and each option carries the id of the range it came from - the price lookup
// (/api/price?heightAttributeId=) keeps working untouched, and the chosen number rides along in
// `value` so the cart and the order email can show "13" instead of "10-15".

// Extra fabric added to the sheet height so it tucks under the mattress. A tucked sheet is priced on
// the height we actually cut (mattress + allowance), not on the mattress alone.
export const TUCK_ALLOWANCE_CM = 5;

// Shown under the chosen height on every surface a cart line appears on: the mini cart, the cart
// page, the checkout summary and the thank-you page. The height beside it stays the mattress height
// the customer picked; this line is what says the sheet is cut taller. Kept here so those four
// surfaces cannot drift apart. The order email carries its own wording (`tuckAllowanceNote` in
// server/api/localisation.js) because it is built server-side and is Bulgarian-only.
const TUCK_ALLOWANCE_NOTE = {
  bg: `+ ${TUCK_ALLOWANCE_CM} см да бъдат добавени към височината`,
  en: `+ ${TUCK_ALLOWANCE_CM} cm added to the height`
};

export function getTuckAllowanceNote(lang) {
  return lang === 'bg' ? TUCK_ALLOWANCE_NOTE.bg : TUCK_ALLOWANCE_NOTE.en;
}

// A cart line only carries `tuckAllowance` for the subcategories that ask the question, and only
// carts built after that question shipped - hence the explicit 'yes' rather than a truthiness check.
export function hasTuckAllowance(cartItem) {
  return Boolean(cartItem) && cartItem.tuckAllowance === 'yes';
}

const RANGE_PATTERN = /^\s*(\d+)\s*-\s*(\d+)\s*$/;

// Guards against a malformed row ("10-9999") blowing the dropdown up to thousands of entries.
const MAX_RANGE_SPAN = 100;

export function parseHeightRange(value) {
  const match = RANGE_PATTERN.exec(String(value));
  if (!match) {
    return null;
  }
  const from = Number(match[1]);
  const to = Number(match[2]);
  if (to < from || to - from > MAX_RANGE_SPAN) {
    return null;
  }
  return { from, to };
}

/**
 * Expand range-valued height attributes into one option per centimetre.
 * Values that aren't ranges (a plain "20") pass through untouched.
 */
export function expandHeightAttributes(heightAttributes) {
  if (!Array.isArray(heightAttributes)) {
    return heightAttributes;
  }

  const expanded = [];
  heightAttributes.forEach((attribute) => {
    const range = parseHeightRange(attribute.value);
    if (!range) {
      expanded.push({ ...attribute, position: Number(attribute.value) });
      return;
    }
    for (let cm = range.from; cm <= range.to; cm++) {
      // `id` stays the range's id - that's what prices are keyed on.
      expanded.push({ ...attribute, value: cm, position: cm, rangeValue: attribute.value });
    }
  });

  return expanded.sort((a, b) => a.position - b.position);
}

/**
 * Find the option - and therefore the price range - that a given height in cm falls into.
 * Used to price a tucked sheet off mattress height + allowance while the customer still sees the
 * mattress height they chose.
 */
export function findHeightOptionForCm(expandedOptions, cm) {
  if (!Array.isArray(expandedOptions) || expandedOptions.length === 0 || !Number.isFinite(cm)) {
    return null;
  }
  const exact = expandedOptions.find((option) => Number(option.value) === cm);
  if (exact) {
    return exact;
  }
  // Options are sorted ascending. The allowance can push a tall mattress past the last range
  // (48 + 5 = 53, and nothing is priced above 50) - there is no higher price to charge, so use the
  // top range rather than leaving the customer with no price at all.
  const lowest = expandedOptions[0];
  const highest = expandedOptions[expandedOptions.length - 1];
  if (cm > Number(highest.value)) {
    return highest;
  }
  if (cm < Number(lowest.value)) {
    return lowest;
  }
  return null;
}

/**
 * A legacy link may carry ?height=10-15. Map it onto the lowest centimetre of that range so the
 * dropdown can still preselect something now that the range itself is no longer an option.
 */
export function resolveHeightPreselect(preselectValue, heightAttributes) {
  if (preselectValue == null || preselectValue === '') {
    return preselectValue;
  }
  const range = parseHeightRange(preselectValue);
  if (!range) {
    return preselectValue;
  }
  const isKnownRange =
    Array.isArray(heightAttributes) &&
    heightAttributes.some((attribute) => String(attribute.value) === String(preselectValue).trim());

  return isKnownRange ? String(range.from) : preselectValue;
}
